import type { ReactNode } from "react";

export interface ReadFieldProps {
  label: string;
  value: string | null | undefined;
  /**
   * `"stacked"` (default): label above a bordered, muted value box; renders an em-dash
   * placeholder when the value is blank — the read-only form-field look used across the
   * detail tiles.
   * `"inline"`: a two-column `<dt>`/`<dd>` row (the host wraps the set in a `<dl>`); the
   * plain value sits beside the label and the whole row is omitted when the value is blank.
   */
  layout?: "stacked" | "inline";
  /** Span both columns of a parent `.fawkes-form-grid` (stacked layout). */
  span2?: boolean;
  className?: string;
}

/** A labelled read-only key/value field — the `fui-readonly-field` vocabulary. Token-styled. */
export function ReadField({ label, value, layout = "stacked", span2, className }: ReadFieldProps): ReactNode {
  if (layout === "inline") {
    if (!value) return null;
    const classes = ["fui-readonly-field", "fui-readonly-field--inline"];
    if (span2) classes.push("fawkes-form-grid__full");
    if (className) classes.push(className);
    return (
      <div className={classes.join(" ")}>
        <dt className="fui-field__label">{label}</dt>
        <dd>{value}</dd>
      </div>
    );
  }
  const classes = ["fui-readonly-field"];
  if (span2) classes.push("fawkes-form-grid__full");
  if (className) classes.push(className);
  return (
    <div className={classes.join(" ")}>
      <span className="fui-field__label">{label}</span>
      <span className="fui-readonly-field__value">{value && value.trim() !== "" ? value : "—"}</span>
    </div>
  );
}
